import type z from 'zod/v4'
import { relations, sql } from 'drizzle-orm'
import { boolean, index, integer, pgTable, text, timestamp, unique, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'

import { numericAsNumber } from '../../../custom-type'
import { organization, user } from '../../auth'
import { Challenge } from '../../content'
import { Course } from '../course'
import { LearnEnrollment } from '../learn-enrollment'

// =============================================================================
// LEADERBOARD ENTRY - FORMER lookup table. NOT READ OR WRITTEN ANY MORE.
// The leaderboard is computed live on every read by `leaderboardBoardLive`
// (packages/trigger/src/metrics/live/leaderboard.ts), which returns the same columns
// and the rank. The producer that filled this table is deleted, so its rows are
// frozen at whatever it last stored — do not read them. The table remains until a
// later migration drops it.
//
// GRAIN (historical): exactly ONE row PER ENROLLMENT (NOT per day), unique on
//   learnEnrollmentId.
// Teacher dashboard metrics (avg grade / avg progress across a teacher's courses)
//   should be built on the live leaderboard formulas, not on this table.
// =============================================================================

export const LeaderboardEntry = pgTable(
  'leaderboard_entry',
  {
    id: uuid().defaultRandom().primaryKey(), // surrogate PK; NOT the upsert key (see uq on learnEnrollmentId)
    // The enrollment this row summarizes. UNIQUE (see uq_..._enrollment below):
    // one leaderboard_entry per LearnEnrollment. This is the conflict target.
    learnEnrollmentId: uuid()
      .references(() => LearnEnrollment.id, { onDelete: 'cascade' })
      .notNull(),
    // Exactly ONE of courseId / challengeId is set, never both, never neither.
    // Practice / Mandiri (lessonModule) enrollments are NOT represented here —
    // if you expect a row for one and it's missing, that's why.
    courseId: uuid().references(() => Course.id, { onDelete: 'cascade' }), // set for course enrollments
    challengeId: uuid().references(() => Challenge.id, { onDelete: 'cascade' }), // set for challenge enrollments
    userId: text() // the learner; denormalized from the enrollment for fast filtering
      .references(() => user.id, { onDelete: 'cascade' })
      .notNull(),
    organizationId: text() // denormalized org scope; teacher dashboards filter on this
      .references(() => organization.id, { onDelete: 'cascade' })
      .notNull(),

    // Pre-computed leaderboard data (all re-derived by the trigger job; UNITS below)
    // UNIT: XP points. = RAW sum(EarnedXp.xpEarned) over this enrollment's graded
    //   progress. The trigger stores the raw sum — it does NOT divide by 100.
    totalXp: numericAsNumber().notNull().default(0), // raw sum(EarnedXp.xpEarned), no /100
    // UNIT: XP points (max attainable). = RAW sum(LessonToSkillWeight.value) across
    //   ALL lessons in the course/challenge (NOT divided by 100). Denominator for gradedXp.
    totalXpPossible: numericAsNumber().notNull().default(0), // raw sum(lessonToSkillWeight.value), no /100
    // UNIT: percentage 0–100. = round((totalXp / totalXpPossible) * 100) (the "grade").
    //   totalXp & totalXpPossible are both raw, so the ratio (and grade) is correct.
    //   Trigger guards div-by-zero: 0 when totalXpPossible == 0 (no weighted lessons yet).
    gradedXp: numericAsNumber().notNull().default(0), // round((totalXp / totalXpPossible) * 100), range 0–100
    // UNIT: percentage 0–100. RECOMPUTED by the trigger as
    //   round((lessonsCompletedCount / totalLessonsCount) * 100). It is NOT copied from
    //   LearnEnrollment.progress (that column is selected but unused), so this value can
    //   differ from the enrollment's own progress — for true avg(LearnEnrollment.progress)
    //   use LessonMetricsDaily.enrollmentProgressSum/Count instead.
    progress: numericAsNumber().notNull().default(0), // = round(lessonsCompletedCount/totalLessonsCount*100), NOT LearnEnrollment.progress
    lessonsCompletedCount: integer().notNull().default(0), // UNIT: count of COMPLETED LearnProgress rows (numerator for `progress`) — counts progress rows, not distinct lessons, so duplicate progress rows for a lesson can inflate it
    totalLessonsCount: integer().notNull().default(0), // UNIT: count of all lessons in course/challenge (denominator)
    isCompleted: boolean().notNull().default(false), // true once the whole enrollment is finished
    completedAt: timestamp({ mode: 'date', withTimezone: true }), // null until isCompleted flips true
    lastActivityAt: timestamp({ mode: 'date', withTimezone: true }), // last learner activity timestamp; drives "recent activity" sorting

    calculatedAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(), // when the trigger job last recomputed this row — staleness check
    createdAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(), // row first inserted (audit only)
    updatedAt: timestamp({ mode: 'date', withTimezone: true }).$onUpdateFn(() => sql`now()`), // auto-bumped on any update (audit only)
  },
  (t) => [
    // GRAIN ENFORCER: one row per enrollment. This is the ON CONFLICT target
    //   the trigger job upserts against — duplicate-row bugs surface as a
    //   unique violation here.
    unique('uq_leaderboard_entry_enrollment').on(t.learnEnrollmentId),
    index('idx_leaderboard_entry_course').on(t.courseId), // lookups by course
    index('idx_leaderboard_entry_challenge').on(t.challengeId), // lookups by challenge
    index('idx_leaderboard_entry_user').on(t.userId), // a user's entries across courses/challenges
    index('idx_leaderboard_entry_org').on(t.organizationId), // teacher dashboard: scope rows to an org
    // Composite (courseId, totalXp): serves the per-course leaderboard ranking
    //   query (filter by course, ORDER BY totalXp DESC). Drop = slow leaderboard.
    index('idx_leaderboard_entry_course_xp').on(t.courseId, t.totalXp),
    // Composite (challengeId, totalXp): same, for per-challenge leaderboards.
    index('idx_leaderboard_entry_challenge_xp').on(t.challengeId, t.totalXp),
  ],
)

export const leaderboardEntryRelations = relations(LeaderboardEntry, ({ one }) => ({
  learnEnrollment: one(LearnEnrollment, {
    fields: [LeaderboardEntry.learnEnrollmentId],
    references: [LearnEnrollment.id],
  }),
  user: one(user, {
    fields: [LeaderboardEntry.userId],
    references: [user.id],
  }),
  organization: one(organization, {
    fields: [LeaderboardEntry.organizationId],
    references: [organization.id],
  }),
  course: one(Course, {
    fields: [LeaderboardEntry.courseId],
    references: [Course.id],
  }),
  challenge: one(Challenge, {
    fields: [LeaderboardEntry.challengeId],
    references: [Challenge.id],
  }),
}))

// drizzle-zod schemas. NOTE what each OMITS — omitted fields are DB-managed and
// will be rejected/ignored if the trigger job tries to pass them.
// INSERT payload: everything EXCEPT id (defaultRandom) + createdAt/updatedAt (DB timestamps).
//   So the trigger job MUST supply learnEnrollmentId, userId, organizationId, the
//   pre-computed metrics, and calculatedAt itself.
export const InsertLeaderboardEntrySchema = createInsertSchema(LeaderboardEntry).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
})

// UPDATE payload: .partial() (all fields optional) and additionally omits the
//   IDENTITY columns (id, learnEnrollmentId, userId, courseId, challengeId,
//   organizationId) + createdAt/updatedAt. I.e. on re-calc you may only mutate
//   the metric columns; the enrollment/course/challenge a row belongs to is fixed.
export const UpdateLeaderboardEntrySchema = createUpdateSchema(LeaderboardEntry).partial().omit({
  id: true,
  learnEnrollmentId: true,
  userId: true,
  courseId: true,
  challengeId: true,
  organizationId: true,
  createdAt: true,
  updatedAt: true,
})

// SELECT schema: full row, nothing omitted — use for reads/validation.
export const SelectLeaderboardEntrySchema = createSelectSchema(LeaderboardEntry)

export type InsertLeaderboardEntry = typeof LeaderboardEntry.$inferInsert
export type UpdateLeaderboardEntry = z.infer<typeof UpdateLeaderboardEntrySchema>
export type SelectLeaderboardEntry = typeof LeaderboardEntry.$inferSelect
